import React from "react";
import { Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Widget } from "@/components/consultation/shared/Widget";
import { useHealthProfile } from "@/hooks/useNutritionEvaluation";
import type { HealthProfile } from "@/types/nutrition";

interface HealthDataRowProps {
  title: string;
  value: string;
  metadata?: string;
}

const HealthDataRow: React.FC<HealthDataRowProps> = ({
  title,
  value,
  metadata,
}) => {
  return (
    <div className="grid grid-cols-[5rem_1fr] items-center gap-2 px-3 py-1.5">
      <p className="z-eyebrow text-muted-foreground">{title}</p>
      <div className="flex flex-wrap items-center gap-1.5 min-w-0">
        <Badge
          variant="secondary"
          className="z-caption tabular-nums h-5 px-2 border-0"
        >
          {value || "-"}
        </Badge>
        {metadata && (
          <Badge variant="warning" className="z-caption h-5 px-2">
            {metadata}
          </Badge>
        )}
      </div>
    </div>
  );
};

interface HealthDataPanelProps {
  appointmentId: string;
}

/**
 * Health Data Panel for Nutrition Evaluation sidebar
 * Shows BMI, TDEE, Waist/Height, Waist/Hip, Ideal BW
 * (matching Flutter's HealthDataView)
 */
export const HealthDataPanel: React.FC<HealthDataPanelProps> = ({
  appointmentId,
}) => {
  const { data: healthProfile, isLoading } = useHealthProfile(appointmentId);

  // Calculate ideal weight difference
  const getIdealWeightRemarks = (profile: HealthProfile | null | undefined) => {
    if (!profile?.weight_in_kgs || !profile?.ideal_weight) return "";
    const diff = profile.weight_in_kgs - profile.ideal_weight;
    const diffStr = diff.toFixed(1);
    return diff >= 0 ? `+ ${diffStr}` : diffStr;
  };

  if (isLoading) {
    return (
      <Widget>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      </Widget>
    );
  }

  return (
    <Widget className="divide-y divide-border/60">
      <HealthDataRow
        title="BMI"
        value={healthProfile?.bmi?.bmi?.toFixed(1) || ""}
        metadata={healthProfile?.bmi?.metadata}
      />
      <HealthDataRow
        title="TDEE"
        value={
          healthProfile?.tdee ? `${healthProfile.tdee.toFixed(1)} kcal` : ""
        }
      />
      <HealthDataRow
        title="Waist/Height"
        value={
          healthProfile?.waist_height_ratio?.waist_height_ratio?.toFixed(1) ||
          ""
        }
        metadata={healthProfile?.waist_height_ratio?.metadata}
      />
      <HealthDataRow
        title="Waist/Hip"
        value={
          healthProfile?.waist_hip_ratio?.waist_hip_ratio?.toFixed(2) || ""
        }
        metadata={healthProfile?.waist_hip_ratio?.metadata}
      />
      <HealthDataRow
        title="Ideal BW(+)"
        value={healthProfile?.ideal_weight?.toFixed(1) || ""}
        metadata={getIdealWeightRemarks(healthProfile)}
      />
    </Widget>
  );
};

/* ------------------------------------------------------------------------ */
/*  Legacy 5-tile grid layout — kept commented for easy revert if the       */
/*  compacted single-widget layout above is rejected. Delete once confirmed. */
/* ------------------------------------------------------------------------ */
// import { Card, CardContent } from "@/components/ui/card";
//
// const HealthDataCard: React.FC<HealthDataRowProps> = ({
//   title,
//   value,
//   metadata,
// }) => (
//   <Card size="sm" className="min-w-28 max-w-48 flex-1 basis-28 shadow-none">
//     <CardContent className="space-y-1">
//       <p className="z-eyebrow text-muted-foreground break-words">{title}</p>
//       <p className="z-body-md z-numeric text-primary">{value || "-"}</p>
//       {metadata && <p className="z-hint text-warning">{metadata}</p>}
//     </CardContent>
//   </Card>
// );
//
// // Legacy return body — wrap above 5 rows in:
// // <div className="flex flex-wrap gap-2">{...HealthDataCard tiles...}</div>
